import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Lock, Mail, ArrowRight, KeyRound, QrCode, Copy, CheckCircle2, Eye, EyeOff } from "lucide-react";

const authHdr = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

export default function PlatformLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState("password"); // password | enroll | otp | recovery
  const [form, setForm] = useState({ email: "", password: "" });
  const [challenge, setChallenge] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [otp, setOtp] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [forgotMsg, setForgotMsg] = useState("");
  const [reenroll, setReenroll] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);

  const startReenroll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/platform/mfa/reenroll/start", { confirm: confirmReplace }, authHdr(challenge));
      setQr(r.data.qr_data_url);
      setReenroll(true);
      setStep("enroll");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const [recCode, setRecCode] = useState("");
  const startRecover = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/platform/mfa/recover/start", { recovery_code: recCode, confirm: confirmReplace }, authHdr(challenge));
      setQr(r.data.qr_data_url);
      setReenroll(true);
      setRecCode("");
      setStep("enroll");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/platform/auth/forgot-password", { email: form.email });
      setForgotMsg(data.message);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/platform/auth/login", form);
      setChallenge(data.challenge_token);
      if (data.mfa_enrolled && data.reenroll_available) {
        setStep("reenroll-offer");
      } else if (data.mfa_enrolled) {
        setStep("otp");
      } else {
        const r = await api.post("/platform/mfa/enroll/start", {}, authHdr(data.challenge_token));
        setQr(r.data.qr_data_url);
        setStep("enroll");
      }
    } catch (err) {
      const c = err?.response?.status;
      toast.error(c === 403 ? "This account is not authorized for Platform Admin." : apiErr(err));
    } finally { setBusy(false); }
  };

  const verifyEnroll = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(reenroll ? "/platform/mfa/reenroll/verify" : "/platform/mfa/enroll/verify", { code }, authHdr(challenge));
      setRecoveryCodes(data.recovery_codes || []);
      setQr("");
      setStep("recovery");
      toast.success(reenroll ? "Authenticator replaced" : "MFA enabled");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = useRecovery ? { recovery_code: otp } : { otp };
      await api.post("/platform/auth/mfa", body, authHdr(challenge));
      toast.success("Welcome to Pawkt Platform Admin");
      navigate("/platform-admin");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  const copyAll = () => { navigator.clipboard?.writeText(recoveryCodes.join("\n")); toast.success("Recovery codes copied"); };

  const shellClass = "min-h-[100dvh] overflow-x-hidden bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-8 sm:p-6";
  const shellStyle = { backgroundImage: "radial-gradient(circle at 15% 20%, rgba(16,185,129,.12) 0, transparent 40%), radial-gradient(circle at 85% 70%, rgba(99,102,241,.12) 0, transparent 40%)" };
  const inputC = "w-full rounded-xl border border-slate-800 bg-slate-900/80 pl-10 pr-3 py-3 text-base sm:text-sm outline-none transition-colors focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";

  return (
    <div className={shellClass} style={shellStyle}>
      <div className="w-full max-w-md rounded-3xl border border-slate-800/80 bg-slate-900/40 p-6 shadow-2xl shadow-emerald-950/30 backdrop-blur-xl sm:p-8" data-testid="pa-login-card">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-xl bg-emerald-500/15 grid place-items-center"><ShieldCheck className="h-5 w-5 text-emerald-400" /></div>
          <div>
            <div className="font-display text-lg font-extrabold tracking-tight">Pawkt Platform</div>
            <div className="text-xs text-slate-400">Developer / Admin Console</div>
          </div>
        </div>

        {step === "password" && (
          <>
            <h1 className="font-display text-2xl font-bold">Platform Admin sign in</h1>
            <p className="text-sm text-slate-400 mt-1">Restricted to authorized Pawkt operators. MFA required.</p>
            <form onSubmit={submitPassword} className="mt-6 space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input data-testid="pa-email" type="email" required placeholder="Work email" aria-label="Work email" autoComplete="username" autoCapitalize="none" spellCheck={false} enterKeyHint="next" autoFocus value={form.email} onChange={set("email")} className={inputC} />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input data-testid="pa-password" type={showPw ? "text" : "password"} required placeholder="Password" aria-label="Password" autoComplete="current-password" enterKeyHint="go" value={form.password} onChange={set("password")} className={`${inputC} pr-12`} />
                <button type="button" data-testid="pa-toggle-password" aria-label={showPw ? "Hide password" : "Show password"} onClick={() => setShowPw(!showPw)} className="absolute right-1 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-lg text-slate-400 hover:text-slate-100">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <button data-testid="pa-submit" disabled={busy} type="submit" className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60">
                {busy ? "Verifying…" : "Continue"} <ArrowRight className="h-4 w-4" />
              </button>
              <button type="button" data-testid="pa-forgot-link" onClick={() => { setForgotMsg(""); setStep("forgot"); }} className="w-full text-center text-xs text-slate-400 hover:text-emerald-300">Forgot password?</button>
            </form>
          </>
        )}

        {step === "forgot" && (
          <>
            <h1 className="font-display text-2xl font-bold">Reset your password</h1>
            <p className="text-sm text-slate-400 mt-1">Enter your Platform Admin email. You will also need your authenticator app or a recovery code.</p>
            {forgotMsg ? (
              <p data-testid="pa-forgot-message" className="mt-6 rounded-lg border border-emerald-800 bg-emerald-950/40 p-3 text-sm text-emerald-200">{forgotMsg}</p>
            ) : (
              <form onSubmit={submitForgot} className="mt-6 space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input data-testid="pa-forgot-email" type="email" required placeholder="Work email" aria-label="Work email" autoComplete="username" autoCapitalize="none" value={form.email} onChange={set("email")} className={inputC} />
                </div>
                <button data-testid="pa-forgot-submit" disabled={busy} type="submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60">
                  {busy ? "Sending…" : "Send reset link"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}
            <button type="button" data-testid="pa-forgot-back" onClick={() => setStep("password")} className="mt-3 w-full text-center text-xs text-slate-400 hover:text-emerald-300">Back to sign in</button>
          </>
        )}

        {step === "enroll" && (
          <>
            <h1 className="font-display text-2xl font-bold flex items-center gap-2"><QrCode className="h-5 w-5 text-emerald-400" /> {reenroll ? "Scan your new authenticator" : "Set up MFA"}</h1>
            <p className="text-sm text-slate-400 mt-1">Scan with Google Authenticator or Authy, then enter the 6-digit code.</p>
            {qr && <img data-testid="pa-mfa-qr" src={qr} alt="MFA QR code" className="mt-4 mx-auto h-44 w-44 rounded-lg bg-white p-2" />}
            <form onSubmit={verifyEnroll} className="mt-4 space-y-3">
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input data-testid="pa-mfa-code" aria-label="Authenticator code" autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} className={inputC} />
              </div>
              <button data-testid="pa-mfa-activate" disabled={busy} type="submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60">
                {busy ? "Activating…" : reenroll ? "Verify and replace" : "Activate MFA"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </>
        )}

        {step === "reenroll-offer" && (
          <div data-testid="pa-reenroll-offer">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2"><QrCode className="h-5 w-5 text-amber-400" /> Replace your authenticator?</h1>
            <p className="text-sm text-slate-400 mt-1">A one-time permission to replace this account's authenticator is active. Your current MFA keeps working until you verify a code from the new app. All old recovery codes will stop working and other sessions will be signed out.</p>
            <label className="mt-4 flex items-start gap-2 text-xs text-slate-300">
              <input type="checkbox" data-testid="pa-reenroll-confirm" checked={confirmReplace} onChange={(e) => setConfirmReplace(e.target.checked)} className="mt-0.5" />
              I confirm I want to replace my authenticator and recovery codes.
            </label>
            <button data-testid="pa-reenroll-start" disabled={busy || !confirmReplace} onClick={startReenroll} className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60">
              {busy ? "Preparing…" : "Replace authenticator"} <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="pa-reenroll-skip" onClick={() => setStep("otp")} className="mt-2 w-full text-center text-xs text-slate-400 hover:text-emerald-300">Keep my current authenticator</button>
          </div>
        )}

        {step === "recovery" && (
          <>
            <h1 className="font-display text-2xl font-bold flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-400" /> Save recovery codes</h1>
            <p className="text-sm text-slate-400 mt-1">Store these one-time codes safely. They are shown only once and let you sign in if you lose your authenticator.</p>
            <div data-testid="pa-recovery-codes" className="mt-4 grid grid-cols-2 gap-2 rounded-lg border border-slate-800 bg-slate-900 p-3 font-mono text-xs">
              {recoveryCodes.map((c) => <div key={c} className="text-emerald-300">{c}</div>)}
            </div>
            <button onClick={copyAll} data-testid="pa-recovery-copy" className="mt-3 flex items-center justify-center gap-2 w-full rounded-lg border border-slate-700 py-2 text-sm text-slate-200 hover:bg-slate-800">
              <Copy className="h-4 w-4" /> Copy codes
            </button>
            <button data-testid="pa-recovery-continue" onClick={() => navigate("/platform-admin")} className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400">
              I've saved them — continue <ArrowRight className="h-4 w-4" />
            </button>
          </>
        )}

        {step === "otp" && (
          <>
            <h1 className="font-display text-2xl font-bold">Two-factor authentication</h1>
            <p className="text-sm text-slate-400 mt-1">{useRecovery ? "Enter one of your recovery codes." : "Enter the 6-digit code from your authenticator app."}</p>
            <form onSubmit={verifyOtp} className="mt-6 space-y-3">
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input data-testid="pa-otp" required autoFocus aria-label={useRecovery ? "Recovery code" : "Authenticator code"} autoComplete="one-time-code" autoCapitalize="none" placeholder={useRecovery ? "recovery code" : "123456"} value={otp} onChange={(e) => setOtp(e.target.value)} className={inputC}
                  inputMode={useRecovery ? "text" : "numeric"} />
              </div>
              <button data-testid="pa-otp-submit" disabled={busy} type="submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60">
                {busy ? "Verifying…" : "Sign in"} <ArrowRight className="h-4 w-4" />
              </button>
              <button type="button" data-testid="pa-toggle-recovery" onClick={() => { setUseRecovery(!useRecovery); setOtp(""); }} className="w-full text-center text-xs text-slate-400 hover:text-emerald-300">
                {useRecovery ? "Use authenticator code instead" : "Use a recovery code instead"}
              </button>
              <button type="button" data-testid="pa-recover-link" onClick={() => { setConfirmReplace(false); setStep("recover"); }} className="w-full text-center text-xs text-slate-500 hover:text-emerald-300">
                Authenticator lost or codes not working? Replace it with a recovery code
              </button>
            </form>
          </>
        )}

        {step === "recover" && (
          <form onSubmit={startRecover} className="space-y-3" data-testid="pa-recover-form">
            <h1 className="font-display text-2xl font-bold">Replace your authenticator</h1>
            <p className="text-sm text-slate-400">Enter one unused recovery code. It is used up. Your current authenticator keeps working until you verify the new one.</p>
            <input data-testid="pa-recover-code" required aria-label="Recovery code" autoCapitalize="none" placeholder="recovery code" value={recCode} onChange={(e) => setRecCode(e.target.value)} className={inputC} />
            <label className="flex items-start gap-2 text-xs text-slate-300">
              <input type="checkbox" data-testid="pa-recover-confirm" checked={confirmReplace} onChange={(e) => setConfirmReplace(e.target.checked)} className="mt-0.5" />
              I want to replace my authenticator app on this account
            </label>
            <button data-testid="pa-recover-submit" disabled={busy || !confirmReplace} type="submit" className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2.5 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60">
              {busy ? "Checking…" : "Continue"} <ArrowRight className="h-4 w-4" />
            </button>
            <button type="button" data-testid="pa-recover-back" onClick={() => setStep("otp")} className="w-full text-center text-xs text-slate-400 hover:text-emerald-300">Back</button>
          </form>
        )}

        <p className="mt-6 text-xs text-slate-500">Platform Admin access is granted only via server allowlist and requires MFA. It is never available from the business app.</p>
      </div>
    </div>
  );
}
